/**
 * <nosto-dynamic-card> — custom element override
 *
 * Defines the element BEFORE Nosto's bundle gets a chance to. Custom
 * element names can only be registered once; first define wins, so this
 * file replaces the default `@nosto/web-components` implementation with
 * one tuned for the Coucou Suzette / Dawn integration:
 *
 *   • Fetch `/products/{handle}?section_id=card-product-nosto`
 *   • Concurrency queue: 5 in-flight on mobile, 10 on desktop
 *   • In-memory cache by handle (single Map shared across instances)
 *   • Viewport-priority ordering when `lazy` mode is on
 *   • 429 / 5xx → exponential backoff retry, max 3 attempts
 *   • 404      → element gets `hidden`, parent `<li>` hidden
 *   • Preserves outer height during refetch to prevent CLS
 *
 * Smala had a similar override (see _nosto/docs/04-web-components.md
 * in the smala repo) — this is the Dawn / Nosto-Search-Templates port.
 *
 * Loaded synchronously in `<head>` BEFORE `content_for_header` so that
 * any `customElements.define('nosto-dynamic-card', ...)` call from the
 * Nosto bundle later is a no-op (we keep ownership of the element).
 */
(function () {
	if (typeof customElements === 'undefined') return;
	if (customElements.get('nosto-dynamic-card')) return;

	// ─── shared state (one per page) ─────────────────────────────────────
	var cache = new Map(); // handle -> resolved HTML
	var pending = new Map(); // handle -> Promise<HTML>
	var queue = []; // [{ priority, run }]
	var running = 0;
	var rateLimitedUntil = 0;

	function isMobile() {
		return window.matchMedia('(max-width: 749px)').matches;
	}
	function maxConcurrency() {
		return isMobile() ? 5 : 10;
	}

	function pump() {
		while (running < maxConcurrency() && queue.length) {
			queue.sort(function (a, b) { return b.priority - a.priority; });
			var task = queue.shift();
			running++;
			Promise.resolve()
				.then(task.run)
				.finally(function () { running--; pump(); });
		}
	}

	function schedule(priority, run) {
		queue.push({ priority: priority, run: run });
		pump();
	}

	function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

	async function fetchSection(handle, attempt) {
		attempt = attempt || 0;
		var now = Date.now();
		if (now < rateLimitedUntil) await sleep(rateLimitedUntil - now);

		// Locale-aware root prefix so `/en/products/...` returns EN
		// card content (titles + product URLs) when the visitor is on the
		// EN market. Without this, EN PLPs would fetch FR HTML and show
		// French product names + FR product links.
		var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
		var url = root + 'products/' + encodeURIComponent(handle) + '?section_id=card-product-nosto';
		var resp = await fetch(url, { credentials: 'same-origin' });

		if (resp.status === 429 || resp.status >= 500) {
			rateLimitedUntil = Date.now() + 3000;
			if (attempt < 3) {
				await sleep(Math.min(2000 * Math.pow(2, attempt), 15000));
				return fetchSection(handle, attempt + 1);
			}
			throw new Error('rate-limited');
		}
		if (resp.status === 404) throw Object.assign(new Error('404'), { code: 404 });
		if (!resp.ok) throw new Error('HTTP ' + resp.status);
		var html = await resp.text();
		// Defensive: section endpoint should return raw section HTML, not a
		// full document. Strip any accidental `<!doctype>` wrappers.
		if (/^<!doctype/i.test(html.trim())) {
			throw new Error('section endpoint returned a full page');
		}
		return html;
	}

	function fetchOnce(handle) {
		var hit = cache.get(handle);
		if (hit) return Promise.resolve(hit);
		var inFlight = pending.get(handle);
		if (inFlight) return inFlight;
		var p = fetchSection(handle).then(function (html) {
			cache.set(handle, html);
			pending.delete(handle);
			return html;
		}, function (err) {
			pending.delete(handle);
			throw err;
		});
		pending.set(handle, p);
		return p;
	}

	// ─── element class ───────────────────────────────────────────────────
	class NostoDynamicCard extends HTMLElement {
		static get observedAttributes() {
			return ['handle', 'section', 'variant-id', 'lazy'];
		}

		// Property setters mirror attributes — Preact / web-components
		// frameworks call setters when they pass props.
		get handle() { return this.getAttribute('handle') || ''; }
		set handle(v) { v ? this.setAttribute('handle', v) : this.removeAttribute('handle'); }
		get section() { return this.getAttribute('section') || ''; }
		set section(v) { v ? this.setAttribute('section', v) : this.removeAttribute('section'); }
		get lazy() { return this.hasAttribute('lazy'); }
		set lazy(v) { v ? this.setAttribute('lazy', '') : this.removeAttribute('lazy'); }

		connectedCallback() {
			if (this._mounted) return;
			this._mounted = true;
			// Show skeleton placeholder immediately so the grid cell has
			// the correct height before the section markup arrives —
			// prevents CLS as cards stream in. The skeleton is replaced
			// by the real card-product HTML once fetch resolves.
			if (!this.innerHTML.trim()) this._renderSkeleton();
			if (this.lazy) this._observeViewport();
			else this._render();
		}

		_renderSkeleton() {
			// Mirrors Dawn's `card-wrapper > card > card__inner` with a
			// pulsing grey block; classes `nosto-skeleton__cell` /
			// `__line` reuse the same animation as the Liquid section
			// skeleton (defined in main-collection-nosto.liquid).
			this.innerHTML =
				'<div class="card-wrapper product-card-wrapper" aria-hidden="true">' +
				'<div class="nosto-skeleton__cell"></div>' +
				'<div class="nosto-skeleton__line"></div>' +
				'<div class="nosto-skeleton__line nosto-skeleton__line--short"></div>' +
				'</div>';
		}

		disconnectedCallback() {
			if (this._observer) {
				this._observer.disconnect();
				this._observer = null;
			}
			// Preserve height if briefly disconnected (e.g. Preact re-render)
			// to avoid CLS jumps.
			var rect = this.getBoundingClientRect();
			if (rect.height > 0) this.style.minHeight = rect.height + 'px';
		}

		attributeChangedCallback(name, oldVal, newVal) {
			if (oldVal === newVal) return;
			if (this._mounted && (name === 'handle' || name === 'section')) {
				this._render();
			}
		}

		_observeViewport() {
			var self = this;
			this._observer = new IntersectionObserver(function (entries) {
				for (var i = 0; i < entries.length; i++) {
					if (entries[i].isIntersecting) {
						self._observer.disconnect();
						self._observer = null;
						self._render();
						return;
					}
				}
			}, { rootMargin: isMobile() ? '800px' : '1500px' });
			this._observer.observe(this);
		}

		async _render() {
			var handle = this.handle;
			var section = this.section;
			if (!handle || !section) return;

			// Compute priority: in-viewport gets +100, top-of-page gets +50
			var rect = this.getBoundingClientRect();
			var priority = (rect.top < window.innerHeight ? 100 : 0) + Math.max(0, 50 - Math.abs(rect.top) / 100);
			var self = this;

			schedule(priority, function () {
				return fetchOnce(handle).then(function (html) {
					if (!self.isConnected) return;
					self.innerHTML = html;
					self.style.minHeight = ''; // release reservation
					self.dispatchEvent(new CustomEvent('nosto-card:rendered', { bubbles: true, detail: { handle: handle } }));
				}, function (err) {
					if (!self.isConnected) return;
					if (err && err.code === 404) {
						self.style.display = 'none';
						var li = self.closest('li.grid__item');
						if (li) li.style.display = 'none';
					}
					self.dispatchEvent(new CustomEvent('nosto-card:error', { bubbles: true, detail: { handle: handle, error: String(err) } }));
				});
			});
		}
	}

	customElements.define('nosto-dynamic-card', NostoDynamicCard);

	// Expose for prefetching from other code (e.g. autocomplete results
	// preloading the SERP cards).
	window.NostoDynamicCard = NostoDynamicCard;
	window.NostoDynamicCard.prefetch = function (handles) {
		(handles || []).forEach(function (h) {
			schedule(0, function () { return fetchOnce(h).catch(function () {}); });
		});
	};
})();
